import { AppRoutes } from "@/routes/config";
import { RouteObject } from "react-router-dom";

import { LoginLayout } from "@/components/layout/LoginLayout";
import { MaintenancePage } from "@/features/maintenance/pages/Maintenance";
import AuthCallback from "@/pages/auth/AuthCallback";
import ConfirmEmail from "@/pages/auth/ConfirmEmail";
import Login from "@/pages/auth/Login";
import Logout from "@/pages/auth/Logout";
import PageNotFound from "@/pages/PageNotFound";
import ServerErrorPage from "@/pages/ServerErrorPage";
import { MatchLayoutWithAuth } from "@/components/layout/MatchLayout/MatchLayout";
import { AppRouteNames } from "@/routes/constants";
import Dashboard from "@/pages/dashboard/Dashboard";
import UserSettingDetail from "@/features/user-settings/pages/UserSettingDetail";
import UserSettingEdit from "@/features/user-settings/pages/UserSettingEdit";
import InformationList from "@/pages/information/InformationList";
import InformationDetail from "@/pages/information/InformationDetail";
import { Navigate } from "react-router-dom";
import { OrganizationLayout } from "@/features/organization/components/OrganizationLayout";
import { OrganizationDetail } from "@/pages/organization/OrganizationDetail";
import { OrganizationEdit } from "@/pages/organization/OrganizationEdit";
import { InvoiceDetail } from "@/pages/organization/InvoiceDetail";
import { InvoiceEdit } from "@/pages/organization/InvoiceEdit";
import { ProductCreate } from "@/pages/organization-product/ProductCreate";
import { ProductDetail } from "@/pages/organization-product/ProductDetail";
import { ProductEdit } from "@/pages/organization-product/ProductEdit";
import { ProductList } from "@/pages/organization-product/ProductList";
import { OrganizationTabPlaceholder } from "@/features/organization/pages/OrganizationTabPlaceholder";
import CompanyForm from "@/features/company/pages/CompanyForm";

export const routes: RouteObject[] = [
  {
    element: <LoginLayout />,
    children: [
      {
        path: AppRoutes.confirmEmail,
        element: <ConfirmEmail />,
      },
    ],
  },
  {
    element: <MatchLayoutWithAuth />,
    children: [
      {
        path: AppRoutes.home,
        element: <Dashboard />,
        id: AppRouteNames.home.value,
      },
      { path: AppRoutes.companyCreate, element: <CompanyForm />, id: AppRouteNames.companyCreate.value },
      { path: AppRoutes.information, element: <InformationList />, id: AppRouteNames.information.value },
      { path: AppRoutes.informationDetail, element: <InformationDetail />, id: AppRouteNames.informationDetail.value },
      {
        path: AppRoutes.userSettingDetail,
        element: <UserSettingDetail />,
        id: AppRouteNames.userSettingDetail.value,
      },
      {
        path: AppRoutes.userSettingEdit,
        element: <UserSettingEdit />,
        id: AppRouteNames.userSettingEdit.value,
      },
      {
        element: <OrganizationLayout />,
        children: [
          {
            path: AppRoutes.organization,
            element: <Navigate to={AppRoutes.organizationProduct} replace />,
          },
          {
            path: AppRoutes.organizationBusiness,
            element: <OrganizationDetail />,
            id: AppRouteNames.organizationBusiness.value,
          },
          {
            path: AppRoutes.organizationBusinessEdit,
            element: <OrganizationEdit />,
            id: AppRouteNames.organizationBusinessEdit.value,
          },
          {
            path: AppRoutes.organizationProduct,
            element: <ProductList />,
            id: AppRouteNames.organizationProduct.value,
          },
          {
            path: AppRoutes.organizationInvoice,
            element: <InvoiceDetail />,
            id: AppRouteNames.organizationInvoice.value,
          },
          {
            path: AppRoutes.organizationInvoiceEdit,
            element: <InvoiceEdit />,
            id: AppRouteNames.organizationInvoiceEdit.value,
          },
          {
            path: AppRoutes.organizationTemplate,
            element: <OrganizationTabPlaceholder />,
            id: AppRouteNames.organizationTemplate.value,
          },
          {
            path: AppRoutes.organizationMail,
            element: <OrganizationTabPlaceholder />,
            id: AppRouteNames.organizationMail.value,
          },
          {
            path: AppRoutes.organizationUser,
            element: <OrganizationTabPlaceholder />,
            id: AppRouteNames.organizationUser.value,
          },
          {
            path: AppRoutes.organizationCsv,
            element: <OrganizationTabPlaceholder />,
            id: AppRouteNames.organizationCsv.value,
          },
          {
            path: AppRoutes.organizationProductLink,
            element: <OrganizationTabPlaceholder />,
            id: AppRouteNames.organizationProductLink.value,
          },
        ],
      },
      // 商品詳細 / 登録・編集 render with the app shell but WITHOUT the 組織設定 tab bar,
      // so they sit outside OrganizationLayout (siblings, not children).
      {
        path: AppRoutes.organizationProductNew,
        element: <ProductCreate />,
        id: AppRouteNames.organizationProductNew.value,
      },
      {
        path: AppRoutes.organizationProductDetail,
        element: <ProductDetail />,
        id: AppRouteNames.organizationProductDetail.value,
      },
      {
        path: AppRoutes.organizationProductEdit,
        element: <ProductEdit />,
        id: AppRouteNames.organizationProductEdit.value,
      },
    ],
  },
  { path: AppRoutes.login, element: <Login /> },
  { path: AppRoutes.authCallback, element: <AuthCallback /> },
  {
    path: AppRoutes.logout,
    element: <Logout />,
  },
  { path: AppRoutes.serverErrorPage, element: <ServerErrorPage /> },
  { path: AppRoutes.maintenance, element: <MaintenancePage /> },
  { path: "*", element: <PageNotFound /> },
];

const flattenRoutes = (routes: RouteObject[]): RouteObject[] => {
  const flatRoutes: RouteObject[] = [];

  const extractChildren = (route: RouteObject) => {
    if (route.children) {
      route.children.forEach((child) => {
        extractChildren(child); // Recursively extract children
      });
    } else {
      flatRoutes.push(route); // Push the route if there are no children
    }
  };

  routes.forEach((route) => extractChildren(route));

  return flatRoutes;
};

export const flattenedRoutes = flattenRoutes(routes);
